import { defineMessages } from 'react-intl';

export const SEGMENTSELECTOR_MESSAGES = defineMessages({
  addSegment: {
    defaultMessage: 'Add',
    description:
      'Aria-label for the + in the "add segment" button to convey the functionality.',
    id: 'SzcXIJL/aWzjy5Ig',
  },
  applicabilityCombinedWarning: {
    defaultMessage: "These segments can't render results in this view.",
    description:
      "Warning shown once below the segment list when more than one selected segment doesn't cover the view's declared data needs.",
    id: 'DuSRQkjfN+FlzOWl',
  },
  applicabilityEntitiesMissingWarning: {
    defaultMessage:
      "Doesn't include the entities required in this view<detail>.</detail>",
    description:
      'Warning shown when the selected segment has no entity coverage at all. No specific types are listed - the fix is the same regardless. The `<detail>` part is dropped in the tooltip, which takes no end punctuation.',
    id: '9MfTMQZEb1FWujn4',
  },
  applicabilityEntityTypeMismatchWarning: {
    defaultMessage:
      'Covers entities, but this tenant has no data for the entity {entityTypesCount, plural, one {type} other {types}} this view needs:<detail> {entityTypes}.</detail>',
    description:
      'Warning shown when the segment covers entities, but the tenant has no data for any of the required entity type(s). The `<detail>` part is dropped where the types are listed instead, e.g. in a tooltip.',
    id: 'q6Qpv+TlJYpXYYtY',
  },
  applicabilityFieldMismatchWarning: {
    defaultMessage:
      'Filters on {fieldsCount, plural, one {a field} other {fields}} this tenant has no {sources} data for:<detail> {fields}.</detail>',
    description:
      'Warning shown when the segment\'s filter references a field missing from the tenant\'s data for the relevant data object(s). `{sources}` is a comma-joined list of the data objects or entity types checked (e.g. "logs" or "logs, spans"). The `<detail>` part is dropped where the fields are listed instead, e.g. in a tooltip.',
    id: 'iNTLXOTc44beRj/7',
  },
  applicabilitySignalsMismatchWarning: {
    defaultMessage:
      "Doesn't include the {dataTypesCount, plural, one {signal} other {signals}} required in this view:<detail> {dataTypes}.</detail>",
    description:
      "Warning shown when the segment covers none of the view's required signal data types. The `<detail>` part is dropped where the types are listed instead, e.g. in a tooltip.",
    id: 'JD+4DustbTxxRolo',
  },
  applicabilityWarningMoreItems: {
    defaultMessage: 'and {count} more',
    description:
      'Closes the truncated list of missing signals, entity types or fields in the applicability warning tooltip.',
    id: '68s36XOc0PAJH8bT',
  },
  applyButton: {
    defaultMessage: 'Apply',
    description:
      'Button in the SegmentSelector overlay to apply the segment selection.',
    id: 'DswaMGT7rt2ajcbI',
  },
  arrayIdentifierError: {
    id: 'eYRqhWFzgj9ne5TC',
    defaultMessage: 'The first column of a variable cannot be an array.',
    description:
      'Error message shown when a segment variable is identified by a first column array.',
  },
  clearAll: {
    defaultMessage: 'Clear all',
    description: 'Label for the clear all button.',
    id: 'e2L/mGCBvRqDTvq2',
  },
  clearSegments: {
    defaultMessage: 'Clear segments',
    description:
      'Label for the clear segments button when segments are applied but the user has no permissions.',
    id: 'WxdPgLpYTL0pyM92',
  },
  copyDetailsButton: {
    id: 'OVHWs31OggLCgWSq',
    defaultMessage: 'Copy details',
    description: 'Label for the copy details button in SegmentsOverlay.',
  },
  copyDetailsHint: {
    id: 'bx7cExKj4X9tpZgs',
    defaultMessage: 'Please copy the details and share them with your admin.',
    description: 'Hint to copy details to share with an admin.',
  },
  defaultAriaLabel: {
    defaultMessage: 'Filter by segments',
    description:
      'Text for SegmentSelector Trigger when no segment is selected yet.',
    id: 'q/AKqzat66d0tRr7',
  },
  docsLinkText: {
    defaultMessage: 'Learn more about segments',
    description: 'Display text for the link to the segments documentation.',
    id: 'ZFLXYT4zoCbKvGNP',
  },
  docsLinkTextShort: {
    defaultMessage: 'Learn more',
    description:
      'Short version of the display text for the link to the segments documentation.',
    id: 'Xxl1F0GNIQlu2LSH',
  },
  emptyString: {
    defaultMessage: 'Empty value',
    description:
      'Display text for segment variable values that are an empty string.',
    id: 'wtm8N4m0hully7OX',
  },
  faasPermissionWarning: {
    id: 'NaaAIQEjAbA5v71n',
    defaultMessage:
      "You'll soon need some additional permissions to continue using segments.",
    description:
      'Warning shown above the segment list, and repeated in the details modal, when a required permission for fetching segment variables is missing but not yet enforced.',
  },
  faasPermissionWarningDetailsHint: {
    id: 'u/zFB5AjGAysdLcT',
    defaultMessage: 'Copy the details and share them with your admin.',
    description: 'Hint to copy details to share with an admin.',
  },
  faasPermissionWarningDetailsButton: {
    id: 'WRJRr2EVKTsJhDcc',
    defaultMessage: 'View details',
    description:
      'Label for the button that opens the FaaS permission warning modal.',
  },
  faasPermissionWarningModalCloseButton: {
    id: 'IxBuNnEpnJSWQsZ1',
    defaultMessage: 'Close',
    description:
      'Label for the button that closes the FaaS permission warning modal.',
  },
  faasPermissionWarningModalTitle: {
    id: 'Yqddnqu0j85Q2t1a',
    defaultMessage:
      "You'll soon need some additional permissions to continue using segments",
    description:
      'Title of the modal opened from the FaaS permission warning "View details" button.',
  },
  filteredSegmentNotFound: {
    defaultMessage:
      'No matching segments found. If you’re looking for a segment value, select the segment first.',
    description:
      "Hint shown when searching for a segment variable that isn't in the list.",
    id: 'ovlsWnQ6TNpxUzyF',
  },
  genericSegmentError: {
    defaultMessage:
      'The applied segments are outdated. Please check your selection in the overlay.',
    description:
      'Error message indicating that something with the current selection is wrong and, therefore, should be checked.',
    id: 'LhnAiI2sqynNbsba',
  },
  invalidVariablesError: {
    defaultMessage:
      "One or more variables don't match the segment's filter criteria. Please update the variables or select a different segment.",
    description:
      "Error message shown when the selected variables are not valid for this segment. They don't match the segment's includes filter.",
    id: 'U8ktDsKphISwBpaV',
  },
  legend: {
    defaultMessage: 'Filter by segments',
    description: 'FieldSet Label in SegmentSelector Overlay.',
    id: '+uTtPJkLATW51CDB',
  },
  linkMenuTrigger: {
    defaultMessage: 'Open to find further resources',
    description:
      'Aria-label for menu trigger for collapsed resource links on mobile.',
    id: 'ymTPa3UIE2oxpoEr',
  },
  loadingAriaLabel: {
    defaultMessage: 'Loading segments',
    description:
      'Aria-label for the loading view within the SegmentSelector Overlay.',
    id: 'iygPRzX1n+gL7TLR',
  },
  manageSegments: {
    defaultMessage: 'Manage segments',
    description: 'Display text for the link to the SegmentsManager app.',
    id: 'MsfOpUQyZ0eEZ/5T',
  },
  moreTooltip: {
    defaultMessage: '+{number} more',
    description:
      'Tooltip message when the tooltip is too big and shows the first selected segment and +X more for the remaining.',
    id: 'GFd2oJ+U46/o0aMy',
  },
  noAccessNoSegments: {
    id: 'eJrsgl5/4XpLIWxo',
    defaultMessage:
      "Your assigned access control policies don't permit you to use segments.",
    description:
      'Message shown when access control permissions are missing and no segments are applied.',
  },
  noReadAccess: {
    id: 'ExRdGipMUK2Tldxd',
    defaultMessage:
      "Segments are applied but your assigned access control policies don't permit you to read them.",
    description:
      'Message shown when access control permissions (read) are missing and segments are already applied.',
  },
  noReadAndWriteAccess: {
    id: 'mtKUJAn0mf0dbG4E',
    defaultMessage:
      "Your assigned access control policies don't permit you to read or apply segments.",
    description:
      'Message shown when access control permissions are missing but a query with segments was run.',
  },
  noRecords: {
    id: 'ZWPrrKMJtr2S3Kx9',
    defaultMessage:
      "The segment values couldn't be fetched. Either keep the selection or remove the segment.",
    description: 'Message shown when the segment values could not be fetched.',
  },
  unavailableRemovedHint: {
    id: 'XsT/ArefqqEMgoPr',
    defaultMessage:
      '{count} unavailable options were removed from your selection.',
    description:
      'Message after removing unavailable options from the selection.',
  },
  unavailableVariableValuesHint: {
    id: 'zZkRTFU9Br/4kme+',
    defaultMessage:
      'Some selected segment values are no longer available and will be removed upon selection.',
    description:
      'Message shown when some selected segment values are no longer available.',
  },
  noSegmentsConfigured: {
    defaultMessage: 'No segments configured.',
    description: 'Message shown in the empty state of the segmentsSelector.',
    id: 'Mexo5VI0BZU5Mw8b',
  },
  noSegmentVariablesAvailable: {
    defaultMessage:
      'No values for this segment available. You can manage this segment in settings.',
    description:
      'Empty state message if no variables for the segment are available.',
    id: 'RoNWrUBQf8gR7lxZ',
  },
  pinButton: {
    defaultMessage: 'Pin',
    description: 'Text for the pin button of a segment preset.',
    id: 'L4FYL0HqCIFzBkzf',
  },
  removedVariablesError: {
    defaultMessage:
      'This segment is no longer using variables. Click apply to refresh it.',
    description:
      'Error message shown when the selected segment has variables but does not expect any.',
    id: 'aGMowcWvdm+5M63D',
  },
  removeSegmentButton: {
    defaultMessage: 'Remove segment',
    description: 'Aria-label for the remove button of a segments select.',
    id: 'S6z4dIJ7FiELVt5X',
  },
  segmentsSelectEmptyState: {
    defaultMessage: 'No other segments available.',
    description: 'Message shown if all available segments have been selected.',
    id: 'CzhNdiuuquVgEUTg',
  },
  segmentsSelectEmptyStateSearch: {
    defaultMessage:
      'No matching segments found. If you’re looking for a segment value, select the segment first.',
    description: 'Message for an empty search result in the segment select.',
    id: 'yJBeHrP0dl2h/JIx',
  },
  segmentsTriggerPlaceholder: {
    defaultMessage: 'Select segment',
    description:
      'Placeholder for the trigger of the select within the segmentSelector Overlay.',
    id: 'oSKhN7fSy19ei878',
  },
  segmentsVariableTriggerPlaceholder: {
    defaultMessage: 'Select value',
    description:
      'Placeholder for the trigger of the select within the segmentSelector Overlay.',
    id: 'RuGHpilISAqb1dDM',
  },
  unavailable: {
    defaultMessage: 'Unavailable',
    description:
      'Description for unavailable segment variables, e.g. if the variable is not available any longer.',
    id: 'K+rYESaxckSqgeF/',
  },
  unavailableSegmentError: {
    defaultMessage:
      "This segment doesn't exist anymore. Please remove it from the selection.",
    description:
      'Error message if the segment was removed but is still selected.',
    id: '+kn/ILSk7yxtVqQ/',
  },
  unlistedSegmentTooltip: {
    defaultMessage:
      'Someone else applied this segment, which is usually not available to you. You can only view or remove it.',
    description: 'Description for the tooltip of an unlisted segment.',
    id: 'UdBwDtbEgD8ojnmL',
  },
  valueMissingError: {
    defaultMessage: 'At least one value must be selected.',
    description: 'Error message shown when no value is selected.',
    id: 'qDnh6SFkeo10shlI',
  },
});
